import type { MetricMetadata } from "@/types/metrics/metadata"

// The "loose" audio metric preset value was renamed to "lenient". Stored metrics
// may still carry "loose", so normalize on read to keep the preset selector in sync.
export const normalizeLegacyPresetValue = (
  value: string | undefined,
): string | undefined => (value === "loose" ? "lenient" : value)

const NAMED_PRESETS = ["strict", "normal", "lenient"] as const
type NamedPreset = (typeof NAMED_PRESETS)[number]

export type PresetSelection =
  | { kind: "named"; value: NamedPreset }
  | { kind: "custom" }
  | { kind: "unknown"; label: string }

export const isNamedPreset = (value: unknown): value is NamedPreset =>
  typeof value === "string" && NAMED_PRESETS.includes(value as NamedPreset)

export const parsePresetSelection = (value: unknown): PresetSelection => {
  if (value === undefined) {
    return { kind: "named", value: "normal" }
  }
  const normalized =
    typeof value === "string" ? normalizeLegacyPresetValue(value) : value
  if (isNamedPreset(normalized)) {
    return { kind: "named", value: normalized }
  }
  return {
    kind: "unknown",
    label:
      typeof value === "string" && value.length > 0
        ? value
        : "Unrecognized preset",
  }
}

export const presetSelectionValue = (
  selection: PresetSelection,
): "strict" | "normal" | "lenient" | "custom" | "unknown" =>
  selection.kind === "named"
    ? selection.value
    : selection.kind === "custom"
      ? "custom"
      : "unknown"

export function readDetectionPreset(
  metadata: MetricMetadata,
  overrideKeys: readonly string[],
  legacyKeys: readonly string[] = [],
): PresetSelection {
  const hasValue = (key: string) => Reflect.get(metadata, key) !== undefined
  const canonical = Reflect.get(metadata, "detection_preset")
  if (canonical !== undefined) {
    const canonicalSelection = parsePresetSelection(canonical)
    if (canonicalSelection.kind !== "named") {
      return canonicalSelection
    }
    if (metadata.threshold_preset === "custom" || overrideKeys.some(hasValue)) {
      return { kind: "custom" }
    }
    return canonicalSelection
  }
  if (metadata.threshold_preset === "custom" || overrideKeys.some(hasValue)) {
    return { kind: "custom" }
  }
  const legacyPreset = Reflect.get(metadata, "threshold_preset")
  if (legacyPreset !== undefined) {
    return parsePresetSelection(legacyPreset)
  }
  return legacyKeys.some(hasValue)
    ? { kind: "custom" }
    : { kind: "named", value: "normal" }
}

export const writeNamedPreset = <T extends object>(
  metadata: T,
  canonicalKey: string,
  legacyKey: string | undefined,
  preset: NamedPreset,
  overrideKeys: readonly string[],
): T => {
  const next = { ...metadata, [canonicalKey]: preset } as T &
    Record<string, unknown>
  if (legacyKey) {
    delete next[legacyKey]
  }
  for (const key of overrideKeys) {
    delete next[key]
  }
  return next as T
}
